import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Search, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { getApiLogs } from "../../../../services/developerService";

const ActivityLogTable = ({
  activeSubTab = "overview",
  searchTerm = "",
  setSearchTerm,
  serviceFilter = "all",
  setServiceFilter,
}) => {
  const { token } = useSelector((state) => state.auth);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, serviceFilter, activeSubTab]);

  useEffect(() => {
    if (token) {
      fetchLogs();
    }
  }, [token, currentPage, activeSubTab, serviceFilter, searchTerm]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const activeService = activeSubTab !== "overview" ? activeSubTab : (serviceFilter !== "all" ? serviceFilter : "");
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(activeService && { service: activeService }),
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await getApiLogs(params, token);
      setLogs(response.logs || []);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error("Failed to fetch paginated activity logs:", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStyle = (srv) => {
    switch (srv) {
      case "TTS": return "bg-amber-100 text-amber-800 border-amber-200";
      case "TTT": return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "STT": return "bg-green-100 text-green-800 border-green-200";
      case "STS": return "bg-orange-100 text-orange-800 border-orange-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleExportAll = async () => {
    try {
      const activeService = activeSubTab !== "overview" ? activeSubTab : (serviceFilter !== "all" ? serviceFilter : "");
      const params = {
        page: 1,
        limit: 500,
        ...(activeService && { service: activeService }),
        ...(searchTerm && { search: searchTerm }),
      };
      const res = await getApiLogs(params, token);
      const jsonStr = JSON.stringify(res.logs || [], null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity_logs_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export logs:", err);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const pageNumbers = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const sorted = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const visible = [];

    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        visible.push(`gap-${page}`);
      }
      visible.push(page);
    });

    return visible;
  })();

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-xl font-bold text-gray-900">
          {activeSubTab === "overview" ? "Activity Across All Services" : "Service Activity"}
        </h3>
        <button
          onClick={handleExportAll}
          className="bg-[#1e6c56] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-emerald-800 transition-colors flex items-center gap-2 self-start sm:self-auto"
        >
          <Download className="w-4 h-4" />
          Export All
        </button>
      </div>

      {/* Search & Service Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search Activity"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1e6c56]"
          />
        </div>
        {activeSubTab === "overview" && (
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1e6c56]"
          >
            <option value="all">All Services</option>
            <option value="TTT">Text-to-Text</option>
            <option value="TTS">Text-to-Speech</option>
            <option value="STT">Speech-to-Text</option>
            <option value="STS">Speech Translations</option>
          </select>
        )}
      </div>

      {/* Activity Table */}
      <div className="overflow-x-auto relative min-h-[160px]">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-xs flex items-center justify-center z-10">
            <Loader2 className="w-6 h-6 text-[#1e6c56] animate-spin" />
          </div>
        )}

        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500">
              <th className="py-3 px-4">Service</th>
              <th className="py-3 px-4">Action</th>
              <th className="py-3 px-4">Token</th>
              <th className="py-3 px-4">When</th>
              <th className="py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {logs.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-8 text-center text-sm text-gray-500">
                  No activity logs found.
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50/80 transition-colors">
                  <td className="py-3.5 px-4">
                    <span className={`inline-block border px-2.5 py-0.5 rounded-full text-xs font-bold ${getBadgeStyle(log.service)}`}>
                      {log.service}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 font-medium text-gray-900">{log.action}</td>
                  <td className="py-3.5 px-4 text-gray-600">{log.token}</td>
                  <td className="py-3.5 px-4 text-gray-500 text-xs">{log.when}</td>
                  <td className="py-3.5 px-4 font-semibold text-emerald-600">{log.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dynamic Pagination Bar */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing <span className="font-semibold text-gray-800">{startIndex + 1}</span> to{" "}
            <span className="font-semibold text-gray-800">
              {Math.min(startIndex + itemsPerPage, totalCount)}
            </span>{" "}
            of <span className="font-semibold text-gray-800">{totalCount}</span> results
          </p>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || loading}
              className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Previous
            </button>

            {pageNumbers.map((pageNum) =>
              typeof pageNum === "string" ? (
                <span key={pageNum} className="px-1.5 text-xs text-gray-400">
                  …
                </span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? "bg-[#1e6c56] text-white"
                      : "text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {pageNum}
                </button>
              )
            )}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || loading}
              className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLogTable;
